package com.isaias.fyattravel.web.components

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import kotlin.js.json
import com.isaias.fyattravel.web.config.APPS_SCRIPT_URL
import com.isaias.fyattravel.web.data.WebProduct
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors

private const val FYAT_WHATSAPP = "+256758250625"
private const val FYAT_EMAIL = "info@fyattraveltour.com"

/**
 * Product Booking Form for Categories (Tour, Flight, Hotel, etc.)
 * Sends booking enquiries via a Google Apps Script Web App
 * (see apps-script/Code.gs) and WhatsApp
 */
@Composable
fun ProductBookingForm(
    product: WebProduct,
    onClose: () -> Unit,
    onSuccess: () -> Unit
) {
    val locale = WebLocaleStore.locale.value
    var fullName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var travelDate by remember { mutableStateOf("") }
    var returnDate by remember { mutableStateOf("") }
    var guests by remember { mutableStateOf("1 Adult") }
    var specialRequests by remember { mutableStateOf("") }
    var website by remember { mutableStateOf("") } // honeypot — must stay empty
    var isSending by remember { mutableStateOf(false) }
    var submitted by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    // Determine service type from product category
    val serviceType = when (product.category?.lowercase()) {
        "flight", "flights" -> "Flight Booking"
        "hotel", "hotels", "stay", "stays" -> "Hotel Booking"
        "car", "cars", "rental" -> "Car Rental"
        "tour", "tours" -> "Tour Package"
        "visa" -> "Visa Assistance"
        else -> "Travel Service"
    }

    fun buildWhatsAppMessage(): String {
        return buildString {
            appendLine("*NEW BOOKING REQUEST - FYAT TRAVEL & TOUR*")
            appendLine("==========================================")
            appendLine()
            appendLine("*PRODUCT DETAILS:*")
            appendLine("Service: $serviceType")
            appendLine("Product: ${product.title}")
            appendLine("Category: ${product.category}")
            product.priceUsd?.let { appendLine("Price: USD $$it") }
            product.priceUgx?.let { appendLine("Price: UGX $it") }
            appendLine()
            appendLine("*CUSTOMER DETAILS:*")
            appendLine("Name: $fullName")
            appendLine("Email: ${email.ifBlank { "Not provided" }}")
            appendLine("Phone: ${phone.ifBlank { "Not provided" }}")
            appendLine()
            appendLine("*BOOKING DETAILS:*")
            appendLine("Travel Date: ${travelDate.ifBlank { "Not specified" }}")
            appendLine("Return Date: ${returnDate.ifBlank { "Not specified" }}")
            appendLine("Guests: $guests")
            if (specialRequests.isNotBlank()) {
                appendLine()
                appendLine("*SPECIAL REQUESTS:*")
                appendLine(specialRequests)
            }
            appendLine()
            appendLine("Please confirm availability and payment options.")
            appendLine("Thank you!")
        }
    }

    fun validate(): Boolean {
        return when {
            fullName.isBlank() -> { validationError = "Please enter your full name"; false }
            email.isBlank() && phone.isBlank() -> { validationError = "Please provide email or phone number"; false }
            else -> { validationError = null; true }
        }
    }

    fun sendEmail() {
        if (!validate()) return
        isSending = true
        validationError = null

        val payload = json(
            "from_name" to fullName,
            "from_email" to if (email.isNotBlank()) email else "Not provided",
            "phone" to if (phone.isNotBlank()) phone else "Not provided",
            "service_type" to serviceType,
            "product_title" to (product.title ?: "Product"),
            "product_category" to (product.category ?: "Service"),
            "product_price_usd" to (product.priceUsd?.toString() ?: "Contact for price"),
            "product_price_ugx" to (product.priceUgx?.toString() ?: "Contact for price"),
            "travel_date" to if (travelDate.isNotBlank()) travelDate else "Not specified",
            "return_date" to if (returnDate.isNotBlank()) returnDate else "Not specified",
            "guests" to guests,
            "message" to if (specialRequests.isNotBlank()) specialRequests else "No special requests",
            "website" to website
        )

        // Content-Type: text/plain (not application/json) so this stays a
        // CORS "simple request" — Apps Script can't answer a preflight.
        window.fetch(
            APPS_SCRIPT_URL,
            RequestInit(
                method = "POST",
                headers = Headers().also { h -> h.append("Content-Type", "text/plain;charset=utf-8") },
                body = kotlin.js.JSON.stringify(payload)
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    if ((obj.result as String?) == "success") {
                        submitted = true
                        onSuccess()
                    } else {
                        val msg = (obj.message as? String) ?: "Unknown error"
                        validationError = "Failed to send email: $msg. Try WhatsApp instead."
                    }
                } catch (t: dynamic) {
                    validationError = "Failed to send email. Try WhatsApp instead."
                } finally {
                    isSending = false
                }
            }
        }.catch { _ ->
            validationError = "Failed to send email — check your connection. Try WhatsApp instead."
            isSending = false
        }
    }

    fun sendWhatsApp() {
        if (!validate()) return
        val message = buildWhatsAppMessage()
        val encoded = js("encodeURIComponent")(message) as String
        window.open("https://wa.me/${FYAT_WHATSAPP.replace("+", "")}?text=$encoded", "_blank")
        submitted = true
        onSuccess()
    }

    // Backdrop
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(ThemeColors.shadow)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(1.em)
        }
        onClick { onClose() }
    }) {
        // Modal content
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                maxWidth(600.px)
                width(100.percent)
                maxHeight(90.vh)
                property("overflow-y", "auto")
                property("box-shadow", "0 25px 50px rgba(0,0,0,0.25)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    padding(1.25.em, 1.5.em)
                    property("border-bottom", "1px solid ${ThemeColors.divider}")
                    property("@media (max-width: 640px)", "{ flex-direction: column; align-items: flex-start; gap: 0.5em; }")
                }
            }) {
                H2({
                    style {
                        fontSize(1.5.em)
                        fontWeight("800")
                        color(ThemeColors.textPrimary)
                        margin(0.px)
                    }
                }) { Text(WebStrings.t(WebStringKey.COMPLETE_BOOKING)) }
                
                Button({
                    style {
                        backgroundColor(Color.transparent)
                        border(0.px)
                        cursor("pointer")
                        fontSize(1.5.em)
                        color(ThemeColors.textSecondary)
                        padding(0.px)
                    }
                    onClick { onClose() }
                }) { Text("×") }
            }

            if (submitted) {
                // Success state
                Div({
                    style {
                        padding(2.em)
                        textAlign("center")
                    }
                }) {
                    Div({
                        style {
                            width(64.px)
                            height(64.px)
                            borderRadius(50.percent)
                            backgroundColor(ThemeColors.successBg)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            margin(0.px)
                            property("margin-bottom", "1em")
                        }
                    }) {
                        Span({
                            style {
                                fontSize(2.em)
                                color(ThemeColors.success)
                            }
                        }) { Text("✓") }
                    }
                    
                    H3({
                        style {
                            fontSize(1.25.em)
                            fontWeight("700")
                            color(ThemeColors.textPrimary)
                            marginBottom(0.5.em)
                        }
                    }) { Text(WebStrings.t(WebStringKey.BOOKING_REQUEST_SENT)) }
                    
                    P({
                        style {
                            color(ThemeColors.textSecondary)
                            marginBottom(1.5.em)
                        }
                    }) { Text(WebStrings.t(WebStringKey.BOOKING_REQUEST_SUBTITLE)) }
                    
                    Button({
                        style {
                            backgroundColor(ThemeColors.primary)
                            color(ThemeColors.textPrimary)
                            padding(0.75.em, 1.5.em)
                            borderRadius(0.5.em)
                            border(0.px)
                            cursor("pointer")
                            fontWeight("600")
                        }
                        onClick { onClose() }
                    }) { Text(WebStrings.t(WebStringKey.CLOSE)) }
                }
            } else {
                // Form content
                Div({
                    style {
                        padding(1.5.em)
                    }
                }) {
                    // Product Summary Card
                    Div({
                        style {
                            backgroundColor(ThemeColors.surface)
                            padding(1.em)
                            borderRadius(0.75.em)
                            marginBottom(1.5.em)
                            display(DisplayStyle.Flex)
                            gap(1.em)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        // Product thumbnail
                        product.thumbnail?.let { url ->
                            if (url.isNotBlank()) {
                                Img(src = url, attrs = {
                                    style {
                                        width(80.px)
                                        height(80.px)
                                        borderRadius(0.5.em)
                                        property("object-fit", "cover")
                                    }
                                })
                            }
                        } ?: Div({
                            style {
                                width(80.px)
                                height(80.px)
                                borderRadius(0.5.em)
                                backgroundColor(ThemeColors.skeleton)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                fontSize(2.em)
                            }
                        }) { 
                            val icon = when (product.category?.lowercase()) {
                                "flight", "flights" -> "✈️"
                                "hotel", "hotels" -> "🏨"
                                "car", "cars" -> "🚗"
                                "tour", "tours" -> "🌍"
                                "visa" -> "🛂"
                                else -> "✨"
                            }
                            Text(icon) 
                        }
                        
                        Div({
                            style {
                                flex(1)
                            }
                        }) {
                            H3({
                                style {
                                    fontSize(1.1.em)
                                    fontWeight("700")
                                    color(ThemeColors.textPrimary)
                                    marginBottom(0.25.em)
                                }
                            }) { Text(product.title ?: "Product") }
                            
                            P({
                                style {
                                    fontSize(0.875.em)
                                    color(ThemeColors.textSecondary)
                                    marginBottom(0.25.em)
                                }
                            }) { Text(serviceType) }
                            
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(0.75.em)
                                }
                            }) {
                                product.priceUsd?.let {
                                    Span({
                                        style {
                                            fontWeight("700")
                                            color(ThemeColors.primary)
                                        }
                                    }) { Text("USD $$it") }
                                }
                                product.priceUgx?.let {
                                    Span({
                                        style {
                                            fontWeight("700")
                                            color(ThemeColors.success)
                                        }
                                    }) { Text("UGX $it") }
                                }
                            }
                        }
                    }

                    // Customer Details Section
                    H4({
                        style {
                            fontSize(1.em)
                            fontWeight("700")
                            color(ThemeColors.textPrimary)
                            marginBottom(1.em)
                        }
                    }) { Text(WebStrings.t(WebStringKey.YOUR_DETAILS_SECTION)) }

                    HoneypotField(value = website, onInput = { website = it })

                    // Full Name
                    FormField("Full Name *") {
                        StyledInput(
                            type = InputType.Text,
                            value = fullName,
                            placeholder = WebStrings.t(WebStringKey.ENTER_YOUR_NAME),
                            onInput = { fullName = it }
                        )
                    }

                    // Email and Phone row
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("1fr 1fr")
                            gap(1.em)
                            marginBottom(1.em)
                            property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                        }
                    }) {
                        FormField("Email") {
                            StyledInput(
                                type = InputType.Email,
                                value = email,
                                placeholder = WebStrings.t(WebStringKey.EMAIL_PLACEHOLDER),
                                onInput = { email = it }
                            )
                        }
                        FormField("Phone *") {
                            StyledInput(
                                type = InputType.Tel,
                                value = phone,
                                placeholder = "+256...",
                                onInput = { phone = it }
                            )
                        }
                    }

                    // Travel Details Section
                    H4({
                        style {
                            fontSize(1.em)
                            fontWeight("700")
                            color(ThemeColors.textPrimary)
                            marginTop(1.em)
                            marginBottom(1.em)
                        }
                    }) { Text(WebStrings.t(WebStringKey.TRAVEL_DETAILS_SECTION)) }

                    // Dates row
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("1fr 1fr")
                            gap(1.em)
                            marginBottom(1.em)
                            property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                        }
                    }) {
                        FormField("Travel Date") {
                            Input(InputType.Date) {
                                style {
                                    width(100.percent)
                                    padding(0.75.em, 1.em)
                                    borderRadius(0.5.em)
                                    property("border", "1px solid ${ThemeColors.border}")
                                    backgroundColor(ThemeColors.surface)
                                    fontSize(0.875.em)
                                    color(ThemeColors.textSecondary)
                                }
                                value(travelDate)
                                onInput { travelDate = it.value }
                            }
                        }
                        FormField("Return Date (if applicable)") {
                            Input(InputType.Date) {
                                style {
                                    width(100.percent)
                                    padding(0.75.em, 1.em)
                                    borderRadius(0.5.em)
                                    property("border", "1px solid ${ThemeColors.border}")
                                    backgroundColor(ThemeColors.surface)
                                    fontSize(0.875.em)
                                    color(ThemeColors.textSecondary)
                                }
                                value(returnDate)
                                onInput { returnDate = it.value }
                            }
                        }
                    }

                    // Guests
                    FormField("Number of Guests") {
                        StyledInput(
                            type = InputType.Text,
                            value = guests,
                            placeholder = WebStrings.t(WebStringKey.GUESTS_EXAMPLE_PLACEHOLDER),
                            onInput = { guests = it }
                        )
                    }

                    // Special Requests
                    FormField("Special Requests or Notes") {
                        TextArea(specialRequests) {
                            style {
                                width(100.percent)
                                padding(0.75.em, 1.em)
                                borderRadius(0.5.em)
                                    property("border", "1px solid ${ThemeColors.border}")
                                    backgroundColor(ThemeColors.surface)
                                fontSize(0.875.em)
                                    color(ThemeColors.textSecondary)
                                height(80.px)
                                property("resize", "vertical")
                            }
                            attr(
                                "placeholder",
                                WebStrings.get(locale, WebStringKey.SPECIAL_REQUIREMENTS_PLACEHOLDER)
                            )
                            onInput { specialRequests = it.value }
                        }
                    }

                    // Validation error
                    validationError?.let { error ->
                        Div({
                            style {
                            backgroundColor(ThemeColors.errorBg)
                            color(ThemeColors.error)
                                padding(0.75.em, 1.em)
                                borderRadius(0.5.em)
                                marginTop(1.em)
                                fontSize(0.875.em)
                                fontWeight("500")
                            }
                        }) { Text(error) }
                    }

                    // Action Buttons - Email and WhatsApp side by side
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            gridTemplateColumns("1fr 1fr")
                            gap(1.em)
                            marginTop(1.5.em)
                            property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                        }
                    }) {
                        // Send via Email button
                        Button({
                            style {
                                backgroundColor(if (isSending) ThemeColors.primaryMuted else ThemeColors.primary)
                                color(ThemeColors.textPrimary)
                                padding(1.em, 1.5.em)
                                borderRadius(0.75.em)
                                border(0.px)
                                cursor(if (isSending) "not-allowed" else "pointer")
                                fontWeight("700")
                                fontSize(1.em)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                gap(0.5.em)
                                opacity(if (isSending) 0.7 else 1)
                            }
                            onClick { if (!isSending) sendEmail() }
                        }) {
                            if (isSending) {
                                Text(WebStrings.t(WebStringKey.SENDING))
                            } else {
                                Text(WebStrings.t(WebStringKey.SEND_VIA_EMAIL))
                            }
                        }

                        // Send via WhatsApp button
                        Button({
                            style {
                                backgroundColor(ThemeColors.whatsapp)
                                color(ThemeColors.textPrimary)
                                padding(1.em, 1.5.em)
                                borderRadius(0.75.em)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("700")
                                fontSize(1.em)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                gap(0.5.em)
                            }
                            onClick { sendWhatsApp() }
                        }) {
                            Text(WebStrings.t(WebStringKey.SEND_VIA_WHATSAPP))
                        }
                    }

                    // Privacy note
                    P({
                        style {
                            fontSize(0.75.em)
                            color(ThemeColors.textMuted)
                            textAlign("center")
                            marginTop(1.em)
                        }
                    }) { 
                        Text(WebStrings.t(WebStringKey.PRIVACY_NOTE))
                    }
                }
            }
        }
    }
}

@Composable
private fun FormField(label: String, content: @Composable () -> Unit) {
    Div({
        style { marginBottom(1.em) }
    }) {
        Label(null, {
            style {
                display(DisplayStyle.Block)
                fontSize(0.875.em)
                fontWeight("600")
                color(ThemeColors.textSecondary)
                marginBottom(0.5.em)
            }
        }) { Text(label) }
        content()
    }
}

@Composable
private fun StyledInput(
    type: InputType<String>,
    value: String,
    placeholder: String,
    onInput: (String) -> Unit
) {
    Input(type) {
        style {
            width(100.percent)
            padding(0.75.em, 1.em)
            borderRadius(0.5.em)
            property("border", "1px solid ${ThemeColors.border}")
            backgroundColor(ThemeColors.surface)
            fontSize(0.875.em)
            color(ThemeColors.textSecondary)
            property("outline", "none")
            property("transition", "border-color 0.2s")
        }
        value(value)
        attr("placeholder", placeholder)
        onInput { onInput(it.value) }
    }
}

/**
 * Spam honeypot: genuinely invisible to sighted and keyboard users (off-screen,
 * not display:none — screen-reading bots that skip display:none often still
 * see off-screen text; tabindex -1 so a real user tabbing through the form
 * never lands on it). Bots that fill in every field will fill this one too;
 * doPost() in apps-script/Code.gs silently drops the submission when it's non-empty.
 */
@Composable
private fun HoneypotField(value: String, onInput: (String) -> Unit) {
    Div({
        style {
            position(Position.Absolute)
            left((-9999).px)
            width(1.px)
            height(1.px)
            overflow("hidden")
        }
    }) {
        Label(attrs = { attr("for", "website-pb") }) { Text("Website") }
        Input(InputType.Text) {
            attr("id", "website-pb")
            attr("name", "website")
            attr("autocomplete", "off")
            attr("tabindex", "-1")
            value(value)
            onInput { onInput(it.value) }
        }
    }
}
